import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Bus, Dumbbell, Brain, Sparkles, School, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import { AFTER_SCHOOL, CONTACT } from "../mock";
import Seo from "../components/Seo";

const ICONS = [Bus, Dumbbell, Brain, Sparkles];

const AfterSchool = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver((es) => es.forEach((e) => e.isIntersecting && e.target.classList.add("is-visible")), { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div className="pt-24">
      <Seo
        title="After-School Martial Arts Program in Alameda, CA | Alameda BJJ"
        description="Our after-school program picks up kids from Alameda elementary schools for Brazilian Jiu-Jitsu, life skills and fitness in a safe, positive dojo. Book a free class."
        path="/after-school"
        breadcrumb={[{ name: "Home", path: "/" }, { name: "After-School", path: "/after-school" }]}
      />
      {/* hero */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={AFTER_SCHOOL.hero} alt="Kids after-school Brazilian Jiu-Jitsu martial arts program in Alameda, CA" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-[#002776]/85" />
        </div>
        <div className="relative max-w-4xl mx-auto px-5 lg:px-8 text-center">
          <span className="inline-block text-sm font-semibold tracking-[0.2em] text-br-yellow uppercase">After-School Program</span>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-extrabold text-white leading-tight">
            We Pick Up. They Grow. You Relax.
          </h1>
          <p className="mt-6 text-lg text-blue-50 max-w-2xl mx-auto leading-relaxed">{AFTER_SCHOOL.intro}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
            <a href={CONTACT.phoneHref}><Button className="h-12 px-8 bg-br-yellow text-[#1A1A1A] hover:bg-[#e6c900] rounded-full font-semibold">Call to Enroll</Button></a>
            <Link to="/contact"><Button variant="outline" className="h-12 px-8 bg-transparent border-white text-white hover:bg-white hover:text-br-blue rounded-full font-semibold">Ask a Question</Button></Link>
          </div>
        </div>
      </section>

      {/* pillars */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto reveal">
            <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A]">More Than Just Childcare</h2>
            <p className="mt-4 text-neutral-600">A structured, enriching afternoon that turns idle hours into growth.</p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {AFTER_SCHOOL.pillars.map((p, i) => {
              const Icon = ICONS[i % ICONS.length];
              return (
                <div key={i} className="reveal bg-white rounded-2xl border border-neutral-100 p-7 card-hover">
                  <div className="h-12 w-12 rounded-xl bg-br-green/10 flex items-center justify-center">
                    <Icon className="text-br-green" size={24} />
                  </div>
                  <h3 className="mt-5 font-display text-lg font-bold text-[#1A1A1A]">{p.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{p.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* schools */}
      <section className="section-pad bg-[#F4F5F1]">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="reveal">
              <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Coverage</span>
              <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A]">We Pick Up From Every Alameda Elementary School</h2>
              <p className="mt-4 text-neutral-600 leading-relaxed">
                Our team collects your child safely from school and brings them straight to the dojo. Don't see your school listed? Just ask — we're always happy to help.
              </p>
              <a href={CONTACT.phoneHref} className="inline-flex items-center gap-2 mt-6 text-br-green font-semibold">
                <Phone size={18} /> {CONTACT.phone}
              </a>
            </div>
            <div className="reveal grid sm:grid-cols-2 gap-3">
              {AFTER_SCHOOL.schools.map((s, i) => (
                <div key={i} className="flex items-center gap-3 bg-white rounded-xl border border-neutral-100 px-4 py-3.5">
                  <School size={18} className="text-br-green shrink-0" />
                  <span className="text-sm font-medium text-neutral-700">{s}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AfterSchool;
